import { Divider, Typography } from '@material-ui/core'
import { Grid } from '@mui/material'
import { navigate, useLocation, useParams } from '@reach/router'
import { navigateToExpiredLink } from '_utils/expired-link-navigation'
import useModal from '_hooks/use-toggle'
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'

import ConfirmationModal from '_components/confirmation-modal'
import Loading from '_components/loading'
import AddProviderModal from '_components/modal/add-provider'
import ModalDialog, { WARNING_MODAL } from '_components/modal/modal-dialog'
import {
  ActionDeadline,
  ServiceOrderCanceledAlert,
  TradesmanAlertInfo,
} from '_components/service-order'
import ProgressBar from '_components/service-order/progress-bar'
import useFetchCall from '_hooks/use-fetch-call'
import useIsCurrentStepStatusCanceled from '_hooks/use-is-current-step-status-canceled'
import useIsGenericParamActive from '_hooks/use-is-generic-param-active'
import { getStepStatusLog, RETURN_BUDGET_PROVIDER } from '_modules/budget/actions'
import { getHistoryLogs, setEventsFilter } from '_modules/history-logs/actions'
import { getEventsFilter, getHistoryLogsLoadingSelector } from '_modules/history-logs/selectors'
import {
  getButtonsAction,
  getGenericParameters,
  getServiceOrder,
  getStepStatusFilter,
  reopenServiceOrder,
  setFromSelectTradesman,
  setOpenProviderModal,
  UPDATE_SERVICE_ORDER,
  updateServiceOrderById,
  updateReferaService,
  updateServiceOrderFlowsOptions,
  VERIFY_NEW_BUDGET,
  verifyNewBudget,
  setOpenDialogFromFlow,
} from '_modules/service-orders/actions'
import {
  activeBudgetSelector,
  currentServiceOrderSelector,
  getOpenProviderModalSelector,
  getStepStatusFilterLoadingSelector,
  getStepStatusFilterSelector,
  getOpenDialogFlowSelector,
} from '_modules/service-orders/selectors'
import { REGISTER_STATUS, ROUTES, STAGE_TYPES } from '_utils/constants'
import { BUTTONS_ACTION, GENERIC_PARAMETERS, STEP_STATUS } from '_utils/constants/service-order'
import { getProviderLimit } from '_/components/modal/add-provider-queue-modal-v2/constants'

import WarningIcon from '@mui/icons-material/Warning'
import { Alert, Button, Loader, useConfirm } from '@refera/ui-web'
import AddCategoryModal from '_/components/modal/add-category-modal'
import AddProviderQueueModal from '_/components/modal/add-provider-queue-modal'
import AddProviderQueueModalV2 from '_/components/modal/add-provider-queue-modal-v2'
import { transformFilterListIntoOptions } from '_/components/modal/add-provider-queue-modal-v2/utils'
import { PoolWarning } from '_/components/PoolWarning'
import SwitchComponent from '_/components/switch'
import { useDialog } from '_/hooks/use-dialog'
import useRolePermission from '_/hooks/use-role-permission'
import { useToast } from '_/hooks/use-toast'
import { getStepStatusLogSelector } from '_/modules/budget/selectors'
import {
  getProviderSuggestionsList,
  getProvidersV2WithoutPagination,
  getProvidersWithoutPagination,
} from '_/modules/provider/actions'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { removeDuplicatedObjectFromArray } from '_/utils/helpers'
import ConfirmRemoveDialog from '_components/dialogs/ConfirmRemoveDialog'
import useCanDoButtonAction from '_hooks/use-can-do-button-action'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { formatErrorMessage } from '../finance/utils/FormatErrorMessage'
import Annotations from './annotations'
import { TAB_OPTIONS } from './annotations/constants'
import DuplicatedTabs from './duplicated-tabs'
import Header from './header'
import { InfoServiceOrderStepStatusModal } from './help-step-status-modal'
import History from './history'
import { ACTIONS, INITIAL_STATE, reducer } from './reducer'
import Estimate from './steps/estimate'
import Request from './steps/request'
import useStyles from './styles'

const ServiceOrder = () => {
  const { serviceOrderId } = useParams()
  const dispatch = useDispatch()
  const isEditing = true

  const { showToast } = useToast()
  const { isAdmin, isSAAS, checkUserPermission } = useRolePermission()

  // const showButtonReferaService = isSAAS
  const showButtonReferaService = false && isSAAS

  // TODO: Rename this constant to isServiceOrderCanceled once this status should be checked by the step_status
  const isServiceOrderCanceledByStepStatus = useIsCurrentStepStatusCanceled()
  const serviceOrder = useSelector(currentServiceOrderSelector)
  const stepStatusFilter = useSelector(getStepStatusFilterSelector)
  const isStepStatusFilterLoading = useSelector(getStepStatusFilterLoadingSelector)
  const isHistoryLogLoading = useSelector(getHistoryLogsLoadingSelector)
  const openDialogFromFlow = useSelector(getOpenDialogFlowSelector)
  const filter = useSelector(getEventsFilter)
  const [modalOpen, handleModal] = useModal()
  const [showRSAlert, setShowRSAlert] = useState(false)
  const [isBudgetEditing, setIsBudgetEditing] = useState(false)
  const agencyContractType = useMemo(() => serviceOrder?.agency.get('contractType'), [serviceOrder])
  const myProvidersSelection = useMemo(
    () => serviceOrder?.agency.get('areaOfActivityData')?.get('myProvidersSelection') ?? null,
    [serviceOrder]
  )
  const { showDialog, closeDialog } = useDialog()

  const methods = useForm()
  const { getValues, setValue } = methods
  const [loading, setLoading] = useState(false)
  const [categoriesUpdated, setCategoriesUpdated] = useState(false)
  const [awaitingProviderModalOpen, setAwaitingProviderModalOpen] = useState(false)
  const isUpdatingRef = useRef(false)

  const parseCategoryIds = so => (so?.get('category')?.toJS?.() || []).map(item => item?.id)

  const serviceOrderCategories = useMemo(() => {
    return parseCategoryIds(serviceOrder)
  }, [serviceOrder])

  const patchCategoriesAndUpdate = useCallback(
    categories => {
      if (isUpdatingRef.current) return
      if (!serviceOrder?.get?.('id')) {
        showToast({ type: 'error', message: 'Chamado não carregado' })
        return
      }
      if (!Array.isArray(categories) || categories.length === 0) {
        showToast({ type: 'error', message: 'Selecione ao menos uma categoria' })
        return
      }

      setValue('categories', categories)
      setLoading(true)
      isUpdatingRef.current = true

      dispatch(updateServiceOrderById(serviceOrderId, { category: categories }))
        .then(() => {
          setCategoriesUpdated(true)
          dispatch(getServiceOrder(serviceOrderId))
        })
        .catch(error => {
          showToast({ type: 'error', message: formatErrorMessage(error) })
          setValue('categories', parseCategoryIds(serviceOrder))
          setLoading(false)
        })
        .finally(() => {
          isUpdatingRef.current = false
        })
    },
    [dispatch, serviceOrderId, setValue, showToast, serviceOrder]
  )

  const handleBudgetEditing = editStatus => {
    setIsBudgetEditing(editStatus)
  }

  // const distributionActive = true
  const distributionActive = useIsGenericParamActive({
    name: GENERIC_PARAMETERS.DISTRIBUTION,
  })

  const [state, localDispatch] = useReducer(reducer, INITIAL_STATE)

  const styles = useStyles({ isEditing, step: serviceOrder?.orderStep })
  const [serviceOrderStatus, setServiceOrderStatus] = useState(STAGE_TYPES.ORDER)
  const isServiceOrderCanceled = useMemo(() => !serviceOrder?.isActive, [serviceOrder?.isActive])
  const [providerOrigin, setProviderOrigin] = useState([])
  const approvedBudgetSelector = useSelector(activeBudgetSelector)
  const openProviderModal = useSelector(getOpenProviderModalSelector)
  const stepStatusLog = useSelector(getStepStatusLogSelector)
  const [dialogMessage, setDialogMessage] = useState('')
  const { isConfirmed } = useConfirm()
  const [modalDialog, setModalDialog] = useState({ isOpen: false, subTitle: '' })

  const canEditIsReferaService = useCanDoButtonAction({
    nameButton: BUTTONS_ACTION.IS_REFERA_SERVICE,
  })

  const { pathname } = useLocation()

  useEffect(() => {
    if (pathname.endsWith('/')) navigate(pathname.slice(0, -1))
  }, [pathname])

  useEffect(() => {
    if (serviceOrder?.agency) {
      const flow = serviceOrder?.toJS().businessFront ? serviceOrder?.toJS().businessFront : {}

      const options = serviceOrder?.agency?.toJS().flows?.map(option => ({
        value: option.name,
        label: option.description,
        disabled: false,
      }))

      if (flow && !options.some(opt => opt.value === flow.name)) {
        options.push({
          value: flow.name,
          label: flow.description,
          disabled: true,
        })
      }
      options.sort((a, b) => a?.label?.localeCompare(b?.label || '') || 0)
      dispatch(updateServiceOrderFlowsOptions(options))
    }
  }, [])

  const shouldShowModalV2 = useMemo(() => {
    return [null, true].includes(myProvidersSelection)
  }, [myProvidersSelection])

  const handleGetServiceOrderActive = useCallback(() => {
    dispatch(getServiceOrder(serviceOrderId))
      .then(() => onGetServiceOrderSuccess())
      .catch(() => {
        navigateToExpiredLink()
      })
  }, [dispatch, serviceOrderId])

  const renderDuplicatedTabs = useMemo(
    () =>
      (serviceOrder?.serviceOrderDuplications?.size > 0 || serviceOrder?.duplicatedFrom) && (
        <DuplicatedTabs serviceOrderId={serviceOrderId} pathname={pathname} />
      ),
    [pathname, serviceOrder?.duplicatedFrom, serviceOrder?.serviceOrderDuplications, serviceOrderId]
  )

  const isDelayedActive = useIsGenericParamActive({
    name: GENERIC_PARAMETERS.DELAYED,
  })

  const isSequencingActive = useIsGenericParamActive({
    name: GENERIC_PARAMETERS.AUTOMATIC_LINKAGE_SEQUENCING,
  })

  const handleHistory = useCallback(() => {
    dispatch(getHistoryLogs(serviceOrderId, filter?.get('mainEvents')))
  }, [dispatch, serviceOrderId, filter])

  const toggleAddProviderModal = useCallback(() => {
    localDispatch({
      type: ACTIONS.TOGGLE_ADD_PROVIDER_MODAL,
    })
  }, [])

  const defaultFilters = useMemo(() => {
    return serviceOrder
      ? {
          ...(serviceOrder.getIn(['property', 'city']) && {
            cities: serviceOrder
              .getIn(['property', 'city'])
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
              .replaceAll(' ', '_'),
          }),
          ...(serviceOrder.category?.first?.()?.get('id') && {
            mainServices: serviceOrder.category?.first?.()?.get('id'),
          }),
          registrationStatus: REGISTER_STATUS.COMPLETE,
          serviceOrder: serviceOrderId,
        }
      : {}
  }, [serviceOrder?.category, serviceOrderId])

  const sendListProviders = useCallback(async () => {
    const dataCategories = getValues('categories')
    setLoading(true)
    setAwaitingProviderModalOpen(true)

    let updatedFilters = defaultFilters

    if (dataCategories.length) {
      // add new categories to filters
      updatedFilters = {
        ...defaultFilters,
        mainServices: [...dataCategories.map(category => category)].join(','),
      }
    }

    if (shouldShowModalV2) {
      const limit = getProviderLimit(agencyContractType)
      dispatch(
        getProvidersV2WithoutPagination({
          filters: { ...updatedFilters, offset: 0, limit },
          attemptMany: true,
        })
      )
        .then(data => {
          setProviderOrigin(transformFilterListIntoOptions(data.origin || []))

          const payload = data.providers.reduce((acc, provider) => {
            if (serviceOrder?.tradesman !== provider.tradesmanId) {
              acc.push(provider.tradesmanId)
            }
            return acc
          }, [])

          if (!payload.length) {
            setLoading(false)
            const errorMessage = data.error?.error_message || formatErrorMessage(data.error)
            showToast({
              type: 'error',
              message: errorMessage,
            })
            handleModal()
            toggleAddProviderModal()

            return
          }
          dispatch(
            getProviderSuggestionsList({
              providers: payload,
              serviceOrderId,
            })
          )
            .then(response => {
              setLoading(false)
              handleModal()
              toggleAddProviderModal()
              if (response?.length === 0) {
                showToast({
                  type: 'error',
                  message:
                    'O processo de sugestão não encontrou prestadores que possam ser automaticamente selecionados para este chamado. Tente ajustar as configurações do chamado ou realize a alocação manual.',
                })
              }
            })
            .catch(() => {
              setLoading(false)
              handleModal()
              toggleAddProviderModal()
              showToast({
                type: 'error',
                message:
                  'Ocorreu um erro no processo de sugestão de prestadores. Tente ajustar as configurações do chamado ou realize a alocação manual.',
              })
            })
        })
        .catch(err => {
          setLoading(false)
          if (err?.debug_error) {
            // CustomValidationError that relates to bad request payload
            showToast({
              type: 'error',
              message: err?.debug_error,
            })
          } else if (err?.error_message) {
            // CustomValidationError that relates to business logic
            showToast({
              type: 'alert',
              message: formatErrorMessage(err),
            })

            // Open the add provider modal if it's a validation error
            handleModal()
            toggleAddProviderModal()
          } else showToast({ type: 'error' }) // Generic error message
        })
    } else {
      dispatch(getProvidersWithoutPagination(updatedFilters))
        .then(data => {
          const payload = data.reduce((acc, provider) => {
            if (serviceOrder?.tradesman !== provider.tradesmanId) {
              acc.push(provider.tradesmanId)
            }

            return acc
          }, [])

          if (!payload.length) {
            setLoading(false)
            handleModal()
            toggleAddProviderModal()

            return
          }
          dispatch(
            getProviderSuggestionsList({
              providers: payload,
              serviceOrderId,
            })
          )
            .then(response => {
              setLoading(false)
              handleModal()
              toggleAddProviderModal()
              if (response?.length === 0) {
                showToast({
                  type: 'error',
                  message:
                    'O processo de sugestão não encontrou prestadores que possam ser automaticamente selecionados para este chamado. Tente ajustar as configurações do chamado ou realize a alocação manual.',
                })
              }
            })
            .catch(() => {
              setLoading(false)
              handleModal()
              toggleAddProviderModal()
              showToast({
                type: 'error',
                message:
                  'Ocorreu um erro no processo de sugestão de prestadores. Tente ajustar as configurações do chamado ou realize a alocação manual.',
              })
            })
        })
        .catch(() => {
          setLoading(false)
          showToast({ type: 'error' })
        })
    }
  }, [dispatch, showToast, defaultFilters, loading, shouldShowModalV2])

  const toggleAddProviderDistributionModal = useCallback(() => {
    if (distributionActive) {
      return handleModal()
    }

    return localDispatch({
      type: ACTIONS.TOGGLE_ADD_PROVIDER_MODAL,
    })
  }, [distributionActive, handleModal])

  const toggleConfirmation = useCallback(() => {
    localDispatch({
      type: ACTIONS.TOGGLE_CONFIRMATION_MODAL,
    })
  }, [])

  const toggleTradesmanAlertInfo = useCallback(() => {
    localDispatch({
      type: ACTIONS.TOGGLE_TRADESMAN_ALERT_INFO,
    })
  }, [])

  const toggleAnnotationDrawerOpen = useCallback(() => {
    localDispatch({
      type: ACTIONS.TOGGLE_ANNOTATION_DRAWER,
      payload: {
        annotationsInitialState: {},
      },
    })
  }, [])

  const handleShowComments = useCallback(() => {
    localDispatch({
      type: ACTIONS.TOGGLE_ANNOTATION_DRAWER_ON_COMMENTS,
      payload: {
        annotationsInitialState: {
          currentTab: TAB_OPTIONS.COMMENTS,
          currentButton: TAB_OPTIONS.COMMENTS.defaultButton,
        },
      },
    })
  }, [])

  const handleShowHelpRequest = useCallback(() => {
    localDispatch({
      type: ACTIONS.UPDATE_STATE,
      payload: {
        annotationsInitialState: {
          currentTab: TAB_OPTIONS.HELP_REQUEST,
          currentButton: TAB_OPTIONS.HELP_REQUEST.defaultButton,
        },
      },
    })
    toggleAnnotationDrawerOpen()
  }, [])

  const updateNewBudgetRejectedModal = useCallback(params => {
    localDispatch({
      type: ACTIONS.UPDATE_NEW_BUDGET_REJECTED_MODAL,
      payload: {
        ...params,
      },
    })
  }, [])

  const onNewBudgetRejected = useCallback(
    response => {
      const error = response?.toJS()?.error || response
      showToast({ type: 'error', message: formatErrorMessage(error) })

      updateNewBudgetRejectedModal({
        isOpen: true,
        subTitle: response.first(),
      })
    },
    [updateNewBudgetRejectedModal, showToast]
  )

  const hasSpecialClassification = useCallback(
    name => {
      const classifications = serviceOrder?.specialClassifications?.toJS()
      if (name === 'Serviço RS')
        return classifications?.some(classification => classification.classificationName === name)

      return classifications?.some(classification => classification.name === name)
    },
    [serviceOrder]
  )

  useEffect(() => {
    if (hasSpecialClassification('Serviço RS')) setShowRSAlert(true)
  }, [hasSpecialClassification, setShowRSAlert])

  const handleSpecialAlert = useMemo(() => {
    if (hasSpecialClassification('contact_refera_forbidden')) {
      return {
        message: 'ATENÇÃO: Cliente que a Refera não pode contatar por solicitação da intermediária',
        color: 'black',
        fontColor: 'white',
      }
    }
    if (hasSpecialClassification('vip_client')) {
      return { message: 'ATENÇÃO: Chamado de um cliente VIP', fontColor: 'black' }
    }
    return null
  }, [hasSpecialClassification])

  const onNewBudgetSuccess = useCallback(() => {
    if (isServiceOrderCanceled) {
      toggleConfirmation()
    } else {
      toggleAddProviderDistributionModal()
    }
  }, [isServiceOrderCanceled, toggleAddProviderDistributionModal])

  const handleSuccessReopenServiceOrderAction = useCallback(() => {
    dispatch(getServiceOrder(serviceOrderId))
      .then(() => onGetServiceOrderSuccess())
      .catch(() => {
        navigateToExpiredLink()
      })
    toggleAddProviderModal()
    toggleConfirmation()
  }, [dispatch, toggleAddProviderModal, toggleConfirmation, serviceOrderId])

  const [isUpdatingServiceOrder] = useFetchCall(UPDATE_SERVICE_ORDER.ACTION)

  const handleAddProvider = useCallback(() => {
    dispatch(reopenServiceOrder(serviceOrderId)).then(() => handleSuccessReopenServiceOrderAction())
  }, [dispatch, serviceOrderId])

  useFetchCall(VERIFY_NEW_BUDGET.ACTION, onNewBudgetSuccess, onNewBudgetRejected)

  const onCloseModal = useCallback(
    updatedProvider => {
      const shouldRefreshHistory =
        updatedProvider === 'updatedProvider' || updatedProvider?.reason === 'success'

      if (shouldRefreshHistory) {
        handleHistory()
      }
      if (modalOpen) {
        handleModal()
      }
      toggleAddProviderModal()
    },
    [handleHistory, toggleAddProviderModal]
  )

  const onGetServiceOrderSuccess = useCallback(() => {
    if (serviceOrder?.tradesman) {
      setServiceOrderStatus(serviceOrder?.stage)
    }
  }, [serviceOrder?.stage, serviceOrder?.tradesman])

  // TODO: Remove this dispatch once the BE is sending the step_status info when updating the budget
  const refetchServiceOrder = useCallback(() => {
    dispatch(getServiceOrder(serviceOrderId))
      .then(() => onGetServiceOrderSuccess())
      .catch(() => {
        navigateToExpiredLink()
      })
  }, [dispatch, serviceOrderId])

  useFetchCall(RETURN_BUDGET_PROVIDER.ACTION, refetchServiceOrder)

  useEffect(() => {
    if (serviceOrderId && serviceOrder === undefined) {
      dispatch(getServiceOrder(serviceOrderId))
        .then(() => onGetServiceOrderSuccess())
        .catch(() => {
          navigateToExpiredLink()
        })
      dispatch(setEventsFilter({ name: 'mainEvents', value: true }))
    }
    dispatch(getHistoryLogs(serviceOrderId, filter?.get('mainEvents')))
  }, [serviceOrderId, filter])

  useEffect(() => {
    if (isUpdatingServiceOrder || awaitingProviderModalOpen) {
      setLoading(true)
    } else {
      setLoading(false)
    }
  }, [isUpdatingServiceOrder, awaitingProviderModalOpen])

  useEffect(() => {
    if (state.isAddProviderModalOpen) {
      if (categoriesUpdated) {
        showToast({ type: 'success', message: 'Categorias atualizadas com sucesso' })
        setCategoriesUpdated(false)
      }
      setAwaitingProviderModalOpen(false)
      setLoading(false)
    }
  }, [state.isAddProviderModalOpen, categoriesUpdated])

  useEffect(() => {
    if (!stepStatusFilter && !isStepStatusFilterLoading) {
      dispatch(getStepStatusFilter())
    }
  }, [dispatch, stepStatusFilter, isStepStatusFilterLoading])

  useEffect(() => {
    if (serviceOrderId) {
      Promise.resolve(dispatch(getStepStatusLog(serviceOrderId))).then(() => {
        dispatch(getButtonsAction())
      })
    }
  }, [serviceOrderId])

  useEffect(() => {
    // dispatch(getButtonsAction())
    dispatch(
      getGenericParameters({
        name: GENERIC_PARAMETERS.DELAYED,
      })
    )
  }, [dispatch])

  useEffect(() => {
    if (serviceOrder?.get('agency')?.get('id')) {
      dispatch(
        getGenericParameters({
          agencyId: serviceOrder.get('agency').get('id'),
          name: GENERIC_PARAMETERS.STEP_STATUS_STRUCTURE,
        })
      )
      dispatch(
        getGenericParameters({
          name: GENERIC_PARAMETERS.PAUSE_SERVICE_ORDER,
        })
      )
      dispatch(
        getGenericParameters({
          name: GENERIC_PARAMETERS.AUTOMATIC_LINKAGE_SEQUENCING,
        })
      )
      dispatch(
        getGenericParameters({
          name: GENERIC_PARAMETERS.DISTRIBUTION,
        })
      )
    }
  }, [dispatch, serviceOrder?.get('agency')?.get('id')])

  useEffect(() => {
    if (serviceOrder?.tradesman) {
      setServiceOrderStatus(STAGE_TYPES.BUDGET)
    }
  }, [dispatch, serviceOrder?.tradesman])

  useEffect(() => {
    methods.register('categories')
  }, [])

  const handleIsReferaServiceChange = useCallback(
    async (newValue, successCallBack = () => {}) => {
      setLoading(true)
      dispatch(updateReferaService(serviceOrderId, { isReferaService: newValue }))
        .then(() => {
          setLoading(false)
          showToast({
            type: 'success',
          })
          successCallBack()
        })
        .catch(error => {
          const errorMessage = error?.response?.data?.error_message
          showToast({
            type: 'error',
            message: errorMessage,
          })
          setLoading(false)
        })
    },
    [serviceOrderId]
  )

  const onIsReferaServiceChange = useCallback(
    (event, componentOnChange) => {
      const isReferaServicePreviousValue = getValues('isReferaService')
      const isReferaServiceNextValue = event.target.checked

      // atualiza e retorna
      if (isReferaServicePreviousValue)
        handleIsReferaServiceChange(isReferaServiceNextValue, () =>
          componentOnChange(isReferaServiceNextValue)
        )
      // pergunta se quer direcionar para refera e se sim, atualiza e retorna
      else
        showDialog({
          type: 'info',
          subject:
            'Você tem certeza que deseja direcionar este chamado para ser atendido pela Refera?',
          labelApprove: 'Sim',
          labelCancel: 'Não',
          onApprove: () =>
            handleIsReferaServiceChange(isReferaServiceNextValue, () => {
              componentOnChange(isReferaServiceNextValue)
              closeDialog()
            }),
          onCancel: closeDialog,
        })
    },
    [methods.watch('isReferaService'), handleIsReferaServiceChange]
  )

  const budgets = useMemo(() => {
    const filteredBudgets = serviceOrder
      ?.toJS()
      .budget?.filter(budget => !budget?.isOriginal && budget !== undefined)

    if (!filteredBudgets?.length) {
      return null
    }

    const budgetsWithoutDuplicates = removeDuplicatedObjectFromArray(filteredBudgets, 'id')

    return isAdmin || isSAAS
      ? budgetsWithoutDuplicates?.filter(
          budget => budget.released || (budget.proposeOpened && budget.diagnosis)
        )
      : budgetsWithoutDuplicates?.filter(
          budget => budget.isRevised || (budget.proposeOpened && budget.diagnosis)
        )
  }, [isAdmin, serviceOrder, isSAAS])

  const activeBudgetHasNoPayerData = useMemo(() => {
    const activeBudget = budgets?.find(budget => budget?.id === serviceOrder?.activeBudget)
    const budgetPayers = activeBudget?.budgetPayer

    const hasNoPayer = budgetPayers?.length === 0

    const isPayerDataEmpty = budgetPayers?.some(
      payer =>
        payer.responsibleName === '' ||
        payer.whatsappResponsible === '' ||
        payer.paymentResponsible === '' ||
        payer.responsibleDocNumber === '' ||
        payer.nameInvoice === '' ||
        payer.formOfPayment === '' ||
        payer.payCollector === '' ||
        payer.installmentQuantity === '' ||
        payer.recipientInvoice === '' ||
        payer.personPayingEmail === ''
    )

    return hasNoPayer || isPayerDataEmpty
  }, [budgets])

  const canSelectTradesman = useCanDoButtonAction({
    nameButton: BUTTONS_ACTION.SELECT_TRADESMAN,
  })
  const canSelectTradesmanInnerBudget = useCanDoButtonAction({
    nameButton: BUTTONS_ACTION.ALLOCATE_TRADESMAN_INNER_BUDGET,
  })

  const handleGoToTriage = useCallback(() => {
    dispatch(setFromSelectTradesman(true))
    navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrder.id}/triagem`)
  }, [serviceOrder?.id])

  const notShowValue = useMemo(() => {
    const step = serviceOrder?.get('stepStatus')
    const stepStatusWhichCannotShowFlows = ['triage', 'waiting_payer_data']
    const canShowFlows = ['notice_to_owner', 'notice_tenant']
    return canShowFlows.includes(step?.name) ? false : stepStatusWhichCannotShowFlows.includes(step)
  }, [serviceOrder])

  const onOpenAddProvider = useCallback(
    async (isSelection = false) => {
      if (!canSelectTradesman) {
        setModalDialog({
          isOpen: true,
          subTitle: 'O chamado não está em uma etapa, que permite seleção de prestadores.',
          type: WARNING_MODAL,
        })
        return
      }
      if (
        serviceOrder?.hasProposeOpened &&
        serviceOrder?.stepStatus !== STEP_STATUS.INNER_BUDGET_REFUSED
      ) {
        if (activeBudgetHasNoPayerData) {
          setModalDialog({
            isOpen: true,
            subTitle:
              'Você não pode selecionar um prestador para um orçamento interno que não possui os dados do pagador. Preencha os dados do pagador e tente novamente.',
            type: WARNING_MODAL,
          })
          return
        }

        if (!canSelectTradesmanInnerBudget && !stepStatusLog?.status === 'inner_budget_refused') {
          setModalDialog({
            isOpen: true,
            subTitle: 'O chamado não está em uma etapa, que permite seleção de prestadores.',
            type: WARNING_MODAL,
          })
          return
        }

        setDialogMessage(
          'Este chamado possui um orçamento interno e esta ação vai enviar tal orçamento para o prestador. Você confirma esta ação?'
        )
        const confirmed = await isConfirmed()
        if (confirmed === false) return
      }

      if (isSelection) {
        const hasCategory = !!serviceOrder?.category?.toJS()[0]
        const hasPriority = !!serviceOrder?.priority
        const hasFlow = !!(serviceOrder?.businessFront && !notShowValue)
        if (!hasCategory || !hasPriority || !hasFlow) {
          handleGoToTriage()
          return
        }
      }

      if (serviceOrder?.id) {
        const checkPermissionName = approvedBudgetSelector
          ? PERMISSIONS.SELECT_TRADESMAN_TO_PREAPPROVED_PROPOSAL
          : PERMISSIONS.SELECT_TRADESMAN

        checkUserPermission({
          permission: checkPermissionName,
          permissionAction: PERMISSIONS_ACTIONS.ADD,
          functionAccess: () => dispatch(verifyNewBudget(serviceOrder?.id)),
        })
      }
    },
    [
      activeBudgetHasNoPayerData,
      canSelectTradesmanInnerBudget,
      canSelectTradesman,
      stepStatusLog,
      serviceOrder?.id,
      serviceOrder?.hasProposeOpened,
      stepStatusLog?.status,
      setDialogMessage,
      approvedBudgetSelector,
      handleGoToTriage,
    ]
  )

  const handleCloseDialog = () => {
    dispatch(setOpenDialogFromFlow(false))
    closeDialog()
  }

  useEffect(() => {
    if (openDialogFromFlow) {
      showDialog({
        type: 'warning',
        subject: 'Você deseja selecionar o prestador agora para executar este serviço?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          handleCloseDialog()
          onOpenAddProvider()
        },
        onCancel: handleCloseDialog,
      })
    }
  }, [dispatch, openDialogFromFlow, onOpenAddProvider])

  const handleProviderModalEffect = useCallback(() => {
    if (openProviderModal) {
      dispatch(setOpenProviderModal(false))
      onOpenAddProvider()
    }
  }, [openProviderModal, onOpenAddProvider, dispatch])

  useEffect(() => {
    handleProviderModalEffect()
  }, [handleProviderModalEffect])

  const handleSelectTradesman = useCallback(() => {
    if (serviceOrder?.tradesman) {
      onOpenAddProvider({ isSelection: true })
      return
    }

    onOpenAddProvider({ isSelection: true })
  }, [onOpenAddProvider, serviceOrder])

  const shouldRenderDefineFlowButton = useMemo(() => {
    const stepStatusList = ['triage', 'waiting_payer_data']
    const soBusinessFront = serviceOrder?.get('businessFront')
    const soStepStatus = serviceOrder?.get('stepStatus')

    return !soBusinessFront || stepStatusList.includes(soStepStatus)
  }, [serviceOrder?.stepStatus])

  const handleDefineFlowClick = useCallback(() => {
    checkUserPermission({
      permission: PERMISSIONS.SERVICE_ORDER,
      permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
      functionAccess: () => {
        dispatch(setFromSelectTradesman(false))
        navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrder?.get('id')}/triagem`)
      },
    })
  }, [serviceOrder?.get('id'), checkUserPermission])

  if (isHistoryLogLoading || !serviceOrder?.get('id')) {
    return (
      <>
        <Helmet>
          <title>{serviceOrderId ? `Chamado ${serviceOrderId}` : 'Refera'}</title>
        </Helmet>
        <Loading />
      </>
    )
  }

  return (
    <Grid className={styles.view}>
      <Helmet>
        <title>{serviceOrderId ? `Chamado ${serviceOrderId}` : 'Refera'}</title>
      </Helmet>
      {dialogMessage && <ConfirmRemoveDialog message={dialogMessage} />}
      <Loader hasBackdrop open={loading} />
      <Grid className={styles.container}>
        <Header
          isEditing={isEditing}
          isBudgetEditing={isBudgetEditing}
          handleGetServiceOrderActive={handleGetServiceOrderActive}
          handleAnnotationsButtonClick={toggleAnnotationDrawerOpen}
          handleShowComments={handleShowComments}
          setLoading={setLoading}
        />
        {renderDuplicatedTabs}
        {!pathname.includes('/orcamento') && (
          <>
            <TradesmanAlertInfo
              open={state.isTradesmanAlertInfoOpen}
              onClose={toggleTradesmanAlertInfo}
            />

            <ServiceOrderCanceledAlert />
          </>
        )}

        {(handleSpecialAlert || showRSAlert) && (
          <Grid container gap={3} className={[styles.mainInfo, styles.alert]}>
            {handleSpecialAlert && (
              <Alert
                style={{ backgroundColor: handleSpecialAlert.color }}
                severity="warning"
                icon={<WarningIcon fontSize="medium" color="warning" />}
                title={
                  <span style={{ color: handleSpecialAlert.fontColor }}>
                    {handleSpecialAlert.message}
                  </span>
                }
              />
            )}
            {showRSAlert && (
              <Alert
                severity="warning"
                icon={<WarningIcon fontSize="medium" color="warning" />}
                title="Chamado de imóvel afetado pela enchente no Rio Grande do Sul."
                onClose={() => {
                  setShowRSAlert(false)
                }}
              />
            )}
          </Grid>
        )}

        <PoolWarning
          stepStatus={serviceOrder?.stepStatus}
          className={`${styles.mainInfo} ${styles.poolWarning}`}
        />

        {!isDelayedActive && <ProgressBar step={serviceOrderStatus} />}
        <Grid container gap={3} className={styles.mainInfo}>
          <Grid item xs={12} container gap={3}>
            {pathname.includes('/orcamento') ? (
              <Grid item xs={12}>
                <Estimate
                  handleShowHelpRequest={handleShowHelpRequest}
                  onEditChange={handleBudgetEditing}
                />
              </Grid>
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '20px',
                    marginBottom: '20px',
                    width: '100%',
                  }}
                >
                  {isDelayedActive && !isServiceOrderCanceledByStepStatus ? (
                    <ActionDeadline handleShowHelpRequest={handleShowHelpRequest} />
                  ) : null}
                  {canEditIsReferaService &&
                    agencyContractType === 'SAAS' &&
                    showButtonReferaService && (
                      <div className={styles.switchInputGroup}>
                        <label className={styles.switchLabel} htmlFor="isReferaService">
                          Atendimento Refera
                        </label>
                        <Controller
                          control={methods.control}
                          name="isReferaService"
                          defaultValue={serviceOrder?.isReferaService || false}
                          render={props => (
                            <SwitchComponent
                              checked={props.value}
                              onChange={e => onIsReferaServiceChange(e, props.onChange)}
                            />
                          )}
                        />
                      </div>
                    )}
                  <Grid
                    container
                    spacing={8}
                    style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}
                  >
                    {agencyContractType === 'SAAS' && <InfoServiceOrderStepStatusModal />}
                    {shouldRenderDefineFlowButton && (
                      <Button onClick={handleDefineFlowClick} color="primary" variant="primary">
                        Definir fluxo
                      </Button>
                    )}
                    <Button onClick={handleSelectTradesman} color="primary" variant="primary">
                      Selecionar prestador
                    </Button>
                  </Grid>
                </div>
                <Grid item xs={12}>
                  <Request />
                </Grid>
              </>
            )}
          </Grid>
          {isServiceOrderCanceled && (
            <Grid item xs={12}>
              <Divider className={styles.divider} />
              <Typography variant="h5" component="p" className={styles.descriptionTitle}>
                Chamado cancelado
              </Typography>
              <Typography color="secondary" variant="body2" component="p">
                Motivo
              </Typography>
              <Typography variant="body1" component="span">
                {serviceOrder?.reasonForCancellation?.get('reason')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
      <History />
      {state.isAddProviderModalOpen && <Loader hasBackdrop open={loading} />}
      {state.isAddProviderModalOpen &&
        isSequencingActive &&
        (shouldShowModalV2 ? (
          <AddProviderQueueModalV2
            handleClose={onCloseModal}
            categories={getValues('categories')}
            isOpen
            defaultProviderOrigin={providerOrigin}
          />
        ) : (
          <AddProviderQueueModal
            handleClose={onCloseModal}
            categories={getValues('categories')}
            isOpen
          />
        ))}
      {state.isAddProviderModalOpen && !isSequencingActive && (
        <AddProviderModal
          onCloseModal={onCloseModal}
          serviceOrder={serviceOrder}
          updateHistoryLog={handleHistory}
          isOpen
        />
      )}
      {state.isConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={state.isConfirmationModalOpen}
          handleClose={toggleConfirmation}
          handleConfirmClick={handleAddProvider}
          handleDeniedClick={toggleConfirmation}
          description="Este chamado está cancelado. Você deseja reativá-lo?"
          cancelButtonText="Não"
          confirmButtonText="Sim"
        />
      )}
      <ModalDialog
        modalDialog={state.newBudgetRejectedModal}
        setModalDialog={updateNewBudgetRejectedModal}
      />
      <Annotations
        handleAnnotationDrawer={toggleAnnotationDrawerOpen}
        open={state.isAnnotationDrawerOpen}
        initialState={state.annotationsInitialState}
      />

      {distributionActive && modalOpen && (
        <FormProvider {...methods}>
          <AddCategoryModal
            value={serviceOrderCategories}
            handleModal={handleModal}
            handleContinue={sendListProviders}
            patchCategories={patchCategoriesAndUpdate}
            title="Categoria da distribuição"
            tradesmanCategories={serviceOrderCategories}
            isLoading={loading}
            submitButtonText="Continuar"
            hasFilter
          />
        </FormProvider>
      )}
      <ModalDialog modalDialog={modalDialog} setModalDialog={setModalDialog} />
    </Grid>
  )
}

export default ServiceOrder
